// Deterministic facts derived from a validated document, shared by the
// policy layer and the compiler: per-variant initial asset set, measures,
// and the capability profile (input of InteractAds' billable product
// classification at publication — CREATIVE-MODEL-V1.md §13.4; the
// classification itself is an OPEN commercial policy and is NOT computed here).

import type { CreativeDocument } from "./schema/document.js";
import { BEHAVIOR_REGISTRY_V1, resolveComponent } from "./registry.js";
import { buildIndex, inLayout, subtree, type DocIndex } from "./validate/context.js";
import type { ResourceLimitKey } from "./policy.js";

/** Assets needed before the initial render of each variant (RUNTIME-CONTRACT §5.1, §10.2). Sorted. */
export function initialAssetSet(index: DocIndex): Record<string, string[]> {
  const doc = index.doc;
  const out: Record<string, string[]> = {};
  const initial = index.scenes.get(doc.initialSceneId)?.value;
  for (const layout of doc.layouts) {
    const keys = new Set<string>();
    if (initial?.background?.kind === "image") keys.add(initial.background.asset);
    const roots = [...(initial?.children ?? []), ...(doc.overlay?.below ?? []), ...(doc.overlay?.above ?? [])];
    for (const id of subtree(index, roots)) {
      if (!inLayout(index, id, layout.id)) continue;
      const el = index.elements.get(id)?.value;
      if (!el || el.template === true) continue;
      if (el.type === "image") keys.add(el.asset);
      if (el.type === "video" && el.poster !== undefined) keys.add(el.poster);
      if (el.type === "text" || el.type === "button") {
        const font = layout.overrides?.[id]?.typography?.font ?? el.typography.font;
        const asset = font === undefined ? undefined : index.fonts.get(font)?.value.asset;
        if (asset !== undefined) keys.add(asset);
      }
    }
    out[layout.id] = [...keys].sort();
  }
  return out;
}

export interface DocumentMeasures {
  readonly counts: Readonly<Partial<Record<ResourceLimitKey, number>>>;
  readonly bytesByKind: Readonly<Record<"image" | "video" | "audio" | "font" | "captions", number>>;
  readonly maxAssetBytesByKind: Readonly<Record<"image" | "video" | "audio" | "font" | "captions", number>>;
  readonly initialBytes: Readonly<Record<string, number>>;
}

export function measure(index: DocIndex): DocumentMeasures {
  const doc = index.doc;
  const tracks = doc.elements.flatMap((e) => e.animations ?? []);
  const overrideTracks = doc.layouts.flatMap((l) =>
    Object.values(l.overrides ?? {}).flatMap((o) => o.animations ?? [])
  );
  const zero = { image: 0, video: 0, audio: 0, font: 0, captions: 0 };
  const bytesByKind = { ...zero };
  const maxAssetBytesByKind = { ...zero };
  let videoMax = 0;
  let audioMax = 0;
  for (const a of doc.assets) {
    bytesByKind[a.kind] += a.bytes;
    maxAssetBytesByKind[a.kind] = Math.max(maxAssetBytesByKind[a.kind], a.bytes);
    if (a.kind === "video") videoMax = Math.max(videoMax, a.durationMs ?? 0);
    if (a.kind === "audio") audioMax = Math.max(audioMax, a.durationMs ?? 0);
  }
  const initial = initialAssetSet(index);
  const initialBytes: Record<string, number> = {};
  for (const [layout, keys] of Object.entries(initial))
    initialBytes[layout] = keys.reduce((sum, k) => sum + (index.assets.get(k)?.value.bytes ?? 0), 0);
  const spawnPools = (doc.rules ?? []).flatMap((r) => r.do.flatMap((a) => (a.type === "spawn" ? [a.poolMax] : [])));
  return {
    counts: {
      layouts: doc.layouts.length,
      scenes: doc.scenes.length,
      elements: doc.elements.length,
      assets: doc.assets.length,
      rules: doc.rules?.length ?? 0,
      actionsPerRule: Math.max(0, ...(doc.rules ?? []).map((r) => r.do.length)),
      animationTracks: tracks.length + overrideTracks.length,
      keyframesTotal: [...tracks, ...overrideTracks].reduce((n, t) => n + t.keyframes.length, 0),
      variables: doc.variables?.length ?? 0,
      timers: doc.timers?.length ?? 0,
      customEvents: doc.events?.length ?? 0,
      surveyQuestions: doc.survey?.questions.length ?? 0,
      spawnPoolMax: Math.max(0, ...spawnPools),
      totalBytes: Object.values(bytesByKind).reduce((a, b) => a + b, 0),
      initialBytes: Math.max(0, ...Object.values(initialBytes)),
      imageBytes: maxAssetBytesByKind.image,
      videoBytes: maxAssetBytesByKind.video,
      audioBytes: maxAssetBytesByKind.audio,
      fontBytes: maxAssetBytesByKind.font,
      videoMaxDurationMs: videoMax,
      audioMaxDurationMs: audioMax,
    },
    bytesByKind,
    maxAssetBytesByKind,
    initialBytes,
  };
}

/**
 * Capability profile: observable facts about what the document USES.
 * Sorted, deterministic, free of tenant data. Classification into a billable
 * product class is InteractAds' server-side policy (OPEN rules, CM-O9).
 */
export interface CapabilityProfile {
  readonly features: readonly string[];
  readonly facts: {
    readonly layouts: number;
    readonly responsiveLayouts: number;
    readonly scenes: number;
    readonly videoElements: number;
    readonly audioElements: number;
    readonly maxVideoDurationMs: number;
    readonly surveyQuestions: number;
    readonly forms: number;
    readonly rules: number;
  };
}

const GAME_ACTIONS = new Set(["spawn", "despawn", "game.start", "game.end", "game.restart"]);

export function capabilityProfile(doc: CreativeDocument, index: DocIndex = buildIndex(doc)): CapabilityProfile {
  const features = new Set<string>();
  const add = (f: string) => features.add(f);
  if (doc.html5) add("html5");
  if (doc.layouts.length > 1) add("multi_layout");
  if (doc.layouts.some((l) => l.sizing.mode === "responsive")) add("responsive");
  if (doc.scenes.length > 1) add("multi_scene");
  for (const el of doc.elements) {
    if (el.type === "video") add("video");
    if (el.type === "audio") add("audio");
    if (el.type === "field") add("form");
    if ((el.animations?.length ?? 0) > 0) add("animation");
    if (el.type === "component") {
      const r = resolveComponent(el.component);
      if (r.ok) r.definition.capabilities.forEach(add);
    }
    for (const b of el.behaviors ?? []) BEHAVIOR_REGISTRY_V1[b.type].capabilities.forEach(add);
  }
  if (doc.layouts.some((l) => Object.values(l.overrides ?? {}).some((o) => (o.animations?.length ?? 0) > 0)))
    add("animation");
  if ((doc.rules?.length ?? 0) > 0) add("interaction");
  const allActions = (doc.rules ?? []).flatMap((r) => r.do);
  if (allActions.some((a) => GAME_ACTIONS.has(a.type))) add("game");
  // Declared game module (7.6F): observable mechanics, so a future commercial
  // classification (CM-O9, OPEN) can tell an advergame from ordinary Rich Media
  // without UI labels. Facts only; no class is decided here.
  const game = doc.game;
  if (game) {
    add("game");
    add("game_module");
    if ((game.targets?.length ?? 0) > 0) add("game_targets");
    if (game.pairs) add("game_pairs");
    if (game.drops) add("game_drops");
    if (game.score !== undefined) add("game_score");
    if (game.lives !== undefined) add("game_lives");
    if (game.timer !== undefined) add("game_timer");
    if (game.pairs?.shuffle === true || (game.targets ?? []).some((t) => t.relocate !== undefined)) add("randomness");
    // Runner (7.6F.1): continuous motion, spawning and collisions are observable mechanics too.
    const runner = game.runner;
    if (runner) {
      add("game_runner");
      add("game_collisions");
      if ((runner.spawns?.length ?? 0) > 0) {
        add("game_spawn");
        add("randomness");
      }
      if (runner.energy) add("game_energy");
      if ((runner.layers?.length ?? 0) > 0) add("parallax");
    }
  }
  if (allActions.some((a) => a.type === "element.relocate" || a.type === "random.choose")) add("randomness");
  if ((doc.variables?.length ?? 0) > 0 || (doc.timers?.length ?? 0) > 0) add("state");
  if ((doc.events?.length ?? 0) > 0) add("custom_events");
  if (doc.survey) add("survey");
  if ((doc.forms?.length ?? 0) > 0) add("form");
  const m = measure(index);
  return {
    features: [...features].sort(),
    facts: {
      layouts: doc.layouts.length,
      responsiveLayouts: doc.layouts.filter((l) => l.sizing.mode === "responsive").length,
      scenes: doc.scenes.length,
      videoElements: doc.elements.filter((e) => e.type === "video").length,
      audioElements: doc.elements.filter((e) => e.type === "audio").length,
      maxVideoDurationMs: m.counts.videoMaxDurationMs ?? 0,
      surveyQuestions: doc.survey?.questions.length ?? 0,
      forms: doc.forms?.length ?? 0,
      rules: doc.rules?.length ?? 0,
    },
  };
}
